// Browser-side Jira service — replaces server/jira-service.ts
// Uses jiraFetch for CORS-safe API calls from the browser
// Indicator calculations are delegated to indicator-runner.ts (same as server)

import { jiraFetch, buildBasicAuth } from "./cors-proxy";
import { runAllIndicators, getDevInfoIndicatorIds } from "./indicator-runner";
import {
  getStatusConfig,
  saveStatusConfig,
  isDoneStatus,
  type StatusCategory,
} from "./jira-status-config";
import type {
  JiraConfig,
  JiraUser,
  JiraBoard,
  JiraSprint,
  JiraIssue,
  JiraChangelogEntry,
  LagIndicatorResult,
  JiraProjectSummary,
  JiraActivityEvent,
  JiraActivityFeed,
} from "./types";

export class BrowserJiraService {
  private baseUrl: string;
  private authHeader: string;
  private config: JiraConfig;

  constructor(config: JiraConfig) {
    const domain = config.domain
      .trim()
      .replace(/^https?:\/\//, "")
      .replace(/\.atlassian\.net\/?$/, "")
      .replace(/\/$/, "")
      .trim();
    const email = config.email.trim();
    const apiToken = config.apiToken.trim();

    this.config = { ...config, domain, email, apiToken };

    if (config.oauthToken && config.cloudId) {
      // Atlassian OAuth2 tokens are scoped to api.atlassian.com — they will 401
      // if sent to the tenant URL (domain.atlassian.net). Must use the cloud API URL.
      this.baseUrl = `https://api.atlassian.com/ex/jira/${config.cloudId}`;
      this.authHeader = `Bearer ${config.oauthToken}`;
      console.log(`[Jira] OAuth2 mode: baseUrl=${this.baseUrl}`);
    } else if (config.oauthToken && !config.cloudId) {
      // Have a token but no cloudId — fall back to tenant URL and warn
      this.baseUrl = `https://${domain}.atlassian.net`;
      this.authHeader = `Bearer ${config.oauthToken}`;
      console.warn(`[Jira] OAuth2 token present but no cloudId — using tenant URL (may 401). Check Atlassian OAuth2 connection in Settings.`);
    } else {
      this.baseUrl = `https://${domain}.atlassian.net`;
      this.authHeader = buildBasicAuth(email, apiToken);
      console.log(`[Jira] API Key mode: baseUrl=${this.baseUrl}, email=${email}`);
    }
  }

  // ==================== HTTP helpers ====================

  private async request<T>(path: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    console.log(`[Jira] ${options.method || "GET"} ${url}`);

    const response = await jiraFetch(url, {
      ...options,
      headers: {
        Authorization: this.authHeader,
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-Atlassian-Token": "no-check",
        ...((options.headers as Record<string, string>) || {}),
      },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      console.error(`[Jira] ${response.status} ${url}: ${text.slice(0, 200)}`);

      if (response.status === 401) {
        // OAuth2 Bearer failed → fall back to Basic auth (API token)
        if (this.authHeader.startsWith("Bearer") && this.config.email && this.config.apiToken) {
          console.warn(`[Jira] OAuth2 Bearer 401 — falling back to Basic auth (API token)`);
          const basicAuth = buildBasicAuth(this.config.email, this.config.apiToken);
          const basicResponse = await jiraFetch(url, {
            ...options,
            headers: {
              Authorization: basicAuth,
              Accept: "application/json",
              "Content-Type": "application/json",
              "X-Atlassian-Token": "no-check",
              ...((options.headers as Record<string, string>) || {}),
            },
          });

          if (basicResponse.ok) {
            this.authHeader = basicAuth;
            console.log(`[Jira] Basic auth fallback succeeded — switching to API token auth`);
            return basicResponse.json() as Promise<T>;
          }

          const basicText = await basicResponse.text().catch(() => "");
          console.error(`[Jira] Basic fallback also failed ${basicResponse.status}: ${basicText.slice(0, 200)}`);
          throw new Error(`Jira API error ${response.status}: ${text || response.statusText}`);
        }

        // Basic auth failed → retry with Bearer (apiToken used as PAT)
        if (this.authHeader.startsWith("Basic") && this.config.apiToken) {
          console.log(`[Jira] Basic 401 — retrying with Bearer (PAT)`);
          const bearerResponse = await jiraFetch(url, {
            ...options,
            headers: {
              Authorization: `Bearer ${this.config.apiToken}`,
              Accept: "application/json",
              "Content-Type": "application/json",
              ...((options.headers as Record<string, string>) || {}),
            },
          });

          if (bearerResponse.ok) {
            this.authHeader = `Bearer ${this.config.apiToken}`;
            console.log(`[Jira] Bearer (PAT) succeeded — switching auth mode`);
            return bearerResponse.json() as Promise<T>;
          }

          const bearerText = await bearerResponse.text().catch(() => "");
          console.error(`[Jira] Bearer (PAT) also failed ${bearerResponse.status}: ${bearerText.slice(0, 200)}`);
          throw new Error(`Jira API error ${response.status}: ${text || response.statusText}`);
        }
      }

      throw new Error(`Jira API error ${response.status}: ${text || response.statusText}`);
    }

    return response.json() as Promise<T>;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private async paginateAgile<T>(path: string, key: string, maxPages = 10): Promise<T[]> {
    const all: T[] = [];
    let startAt = 0;
    const maxResults = 50;

    for (let page = 0; page < maxPages; page++) {
      const separator = path.includes("?") ? "&" : "?";
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await this.request<any>(`${path}${separator}startAt=${startAt}&maxResults=${maxResults}`);
      const items = data[key] || [];
      all.push(...items);

      if (data.isLast !== false && startAt + items.length >= (data.total || items.length)) {
        break;
      }
      startAt += maxResults;
    }

    return all;
  }

  private async paginateSearch(jql: string, fields: string[], expand?: string[], maxPages = 20): Promise<JiraIssue[]> {
    const all: JiraIssue[] = [];
    let nextPageToken: string | undefined = undefined;
    const maxResults = 100;

    for (let page = 0; page < maxPages; page++) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const body: any = { jql, maxResults, fields };
      if (expand && expand.length > 0) {
        body.expand = expand.join(",");
      }
      if (nextPageToken) {
        body.nextPageToken = nextPageToken;
      }

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await this.request<any>("/rest/api/3/search/jql", {
        method: "POST",
        body: JSON.stringify(body),
      });

      const issues = data.issues || [];
      all.push(...issues);

      if (data.nextPageToken) {
        nextPageToken = data.nextPageToken;
      } else {
        break;
      }
    }

    return all;
  }

  // ==================== Auth / validation ====================

  async validateCredentials(): Promise<{ valid: boolean; user?: JiraUser; error?: string }> {
    try {
      const user = await this.request<JiraUser>("/rest/api/3/myself");
      return { valid: true, user };
    } catch (e: unknown) {
      const err = e as Error;
      return { valid: false, error: err.message };
    }
  }

  // ==================== Status Sync ====================

  /** Fetch all available statuses from Jira and merge into the status config.
   *  Uses Jira's statusCategory (done/new/indeterminate) as a primary signal,
   *  then applies keyword heuristics for finer-grained categorization. */
  async syncStatuses(): Promise<{ synced: number; newStatuses: string[] }> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const jiraStatuses = await this.request<any[]>("/rest/api/3/status");
    const statusNames = jiraStatuses.map((s) => s.name as string);
    const config = await getStatusConfig();
    const allExisting = new Set(
      Object.values(config.categories).flat().map((s) => s.toLowerCase())
    );

    const newStatuses: string[] = [];
    for (const jStatus of jiraStatuses) {
      const name = (jStatus.name as string).toLowerCase();
      if (allExisting.has(name)) continue;

      // Jira statusCategory.key: 'done' | 'indeterminate' | 'new' | 'undefined'
      const catKey = jStatus.statusCategory?.key as string | undefined;
      const targetCategory = this.guessStatusCategory(name, catKey);

      config.categories[targetCategory].push(name);
      newStatuses.push(name);
    }

    config.jiraStatuses = statusNames;
    config.lastSynced = new Date().toISOString();
    await saveStatusConfig(config);

    console.log(`[Jira] Synced ${statusNames.length} statuses, ${newStatuses.length} new`);
    return { synced: statusNames.length, newStatuses };
  }

  /** Keyword-based heuristic to categorize a Jira status name.
   *  catKey is Jira's own broad category: 'done', 'indeterminate', or 'new'. */
  private guessStatusCategory(name: string, catKey?: string): StatusCategory {
    const n = name.toLowerCase();

    // ---------- done-family (Jira says done, or keyword match) ----------
    if (catKey === 'done') {
      // Even within "done", some might be cancelled
      if (n.includes('cancel') || n.includes('reject') || n.includes('won\'t do') || n.includes('duplicate')) return 'cancelled';
      return 'done';
    }

    // ---------- keyword heuristics (checked before falling back to catKey) ----------

    // Blocked / failed
    if (n.includes('block') || n.includes('pending db') || n.includes('deployment failed') || n.includes('stuck') || n.includes('on hold') || n.includes('impediment')) return 'blocked';

    // Cancelled
    if (n.includes('cancel') || n.includes('reject') || n.includes('won\'t do') || n.includes('duplicate') || n.includes('withdrawn')) return 'cancelled';

    // Hotfix
    if (n.includes('hotfix') || n.includes('hot fix') || n.includes('emergency')) return 'hotfix';

    // Rework
    if (n.includes('rework') || n.includes('returned') || n.includes('reopened') || n.includes('sent back')) return 'rework';

    // Testing / QA
    if (n.includes('test') || n.includes('qa') || n.includes('pre-prod') || n.includes('pre prod') || n.includes('review') || n.includes('verification') || n.includes('validation') || n.includes('audit')) return 'testing';

    // Release candidate / deployment
    if (n.includes('release') || n.includes('deploy') || n.includes('ready for') || n.includes('staging') || n.includes('uat')) return 'release_candidate';

    // Done keywords (in case Jira didn't mark it as done)
    if (n.includes('complete') || n.includes('done') || n.includes('resolved') || n.includes('closed') || n.includes('live in') || n.includes('archived') || n.includes('shipped') || n.includes('delivered') || n.includes('production')) return 'done';

    // In progress keywords
    if (n.includes('progress') || n.includes('development') || n.includes('coding') || n.includes('implementing') || n.includes('working') || n.includes('active') || n.includes('dev ')) return 'in_progress';

    // ---------- fall back to Jira's broad category ----------
    if (catKey === 'indeterminate') return 'in_progress';
    if (catKey === 'new') return 'todo';

    return 'todo';
  }

  // ==================== Boards & Sprints ====================

  async getBoards(): Promise<JiraBoard[]> {
    return this.paginateAgile<JiraBoard>("/rest/agile/1.0/board", "values");
  }

  async getQuickFilters(boardId: number): Promise<{ id: number; name: string; jql: string; description?: string; position: number }[]> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await this.paginateAgile<any>(`/rest/agile/1.0/board/${boardId}/quickfilter`, "values");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data.map((qf: any) => ({ id: qf.id, name: qf.name, jql: qf.jql || '', description: qf.description, position: qf.position ?? 0 }));
    } catch (e) {
      console.warn('[Jira] Failed to fetch quick filters:', e);
      return [];
    }
  }

  async getBoardForProject(projectKey: string): Promise<JiraBoard | null> {
    try {
      const boards = await this.paginateAgile<JiraBoard>(`/rest/agile/1.0/board?projectKeyOrId=${encodeURIComponent(projectKey)}`, "values", 1);
      return boards[0] || null;
    } catch {
      return null;
    }
  }

  async getSprints(boardId: number): Promise<JiraSprint[]> {
    return this.paginateAgile<JiraSprint>(`/rest/agile/1.0/board/${boardId}/sprint`, "values");
  }

  async getActiveSprint(boardId: number): Promise<JiraSprint | null> {
    const sprints = await this.getSprints(boardId);
    return sprints.find((s) => s.state === "active") || null;
  }

  async getSprintIssueCount(sprintId: number): Promise<{ total: number; done: number }> {
    try {
      // Single request — maxResults=200 returns issues + total count in one call
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await this.request<any>(`/rest/agile/1.0/sprint/${sprintId}/issue?maxResults=200&fields=status`);
      const total = data.total || 0;
      if (total === 0) return { total: 0, done: 0 };
      const issues = data.issues || [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const done = issues.filter((i: any) => {
        const cat = i.fields?.status?.statusCategory?.key;
        const name = i.fields?.status?.name?.toLowerCase() || "";
        return cat === "done" || name === "done" || name === "closed" || name === "resolved";
      }).length;
      return { total, done };
    } catch {
      return { total: 0, done: 0 };
    }
  }

  // ==================== Users ====================

  async getProjectUsers(projectKey: string): Promise<{ accountId: string; displayName: string; avatarUrl?: string; email?: string }[]> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const users = await this.request<any[]>(
        `/rest/api/3/user/assignable/search?project=${encodeURIComponent(projectKey)}&maxResults=1000`
      );
      return (users || [])
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .filter((u: any) => u.accountType === "atlassian" && u.active !== false)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((u: any) => ({
          accountId: u.accountId,
          displayName: u.displayName,
          avatarUrl: u.avatarUrls?.["32x32"] || u.avatarUrls?.["48x48"],
          email: u.emailAddress,
        }));
    } catch (e) {
      console.error("[Jira] Failed to fetch project users:", e);
      return [];
    }
  }

  // ==================== Fields ====================

  async getFields(): Promise<{ id: string; name: string; custom: boolean }[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const raw = await this.request<any[]>('/rest/api/3/field');
    return raw.map(f => ({ id: f.id as string, name: f.name as string, custom: !!f.custom }));
  }

  // ==================== Issues ====================

  async searchIssues(jql: string, fields?: string[]): Promise<JiraIssue[]> {
    const defaultFields = [
      "summary", "status", "issuetype", "assignee", "reporter",
      "priority", "created", "updated", "resolutiondate",
      "components", "labels", "sprint",
      "timeoriginalestimate", "timespent",
    ];
    return this.paginateSearch(jql, fields || defaultFields);
  }

  async searchIssuesWithChangelog(jql: string, fields?: string[]): Promise<JiraIssue[]> {
    const defaultFields = [
      "summary", "status", "issuetype", "assignee", "reporter",
      "priority", "created", "updated", "resolutiondate",
      "components", "labels", "sprint",
      "timeoriginalestimate", "timespent",
    ];
    return this.paginateSearch(jql, fields || defaultFields, ["changelog"]);
  }

  async getIssueChangelog(issueKey: string): Promise<JiraChangelogEntry[]> {
    const all: JiraChangelogEntry[] = [];
    let startAt = 0;
    const maxResults = 100;

    for (let page = 0; page < 10; page++) {
      const data = await this.request<{ values: JiraChangelogEntry[]; total: number }>(
        `/rest/api/3/issue/${issueKey}/changelog?startAt=${startAt}&maxResults=${maxResults}`
      );
      all.push(...data.values);
      if (startAt + data.values.length >= data.total) break;
      startAt += maxResults;
    }

    return all;
  }

  // ==================== Projects ====================

  async getProjects(): Promise<{ id: string; key: string; name: string }[]> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = await this.request<any[]>("/rest/api/3/project?expand=description");
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return data.map((p: any) => ({ id: p.id, key: p.key, name: p.name }));
  }

  async getProjectSummary(projectKey: string, boardId?: number): Promise<JiraProjectSummary> {
    const jql = `project = "${projectKey}" ORDER BY updated DESC`;
    const issues = await this.searchIssues(jql);

    const completed = issues.filter((i) => i.fields.status.statusCategory.key === "done");
    const inProgress = issues.filter((i) => i.fields.status.statusCategory.key === "indeterminate");

    let sprints: JiraSprint[] = [];
    if (boardId) {
      sprints = await this.getSprints(boardId);
    }

    return {
      projectKey,
      projectName: projectKey,
      boardId,
      totalIssues: issues.length,
      completedIssues: completed.length,
      inProgressIssues: inProgress.length,
      sprints,
    };
  }

  // ==================== Full Issue Detail ====================

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async getIssueDetail(issueKey: string): Promise<any> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const issue = await this.request<any>(
      `/rest/api/3/issue/${issueKey}?expand=renderedFields,names,changelog`
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let comments: any[] = [];
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const commentData = await this.request<any>(
        `/rest/api/3/issue/${issueKey}/comment?orderBy=-created&maxResults=50&expand=renderedBody`
      );
      comments = commentData.comments || [];
    } catch { /* empty */ }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let remoteLinks: any[] = [];
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      remoteLinks = await this.request<any[]>(
        `/rest/api/3/issue/${issueKey}/remotelink`
      );
    } catch { /* empty */ }

    const [devInfo, devDetail] = await Promise.all([
      this.getIssueDevInfo(issue.id),
      this.getIssueDevDetail(issue.id),
    ]);

    const f = issue.fields || {};
    const rf = issue.renderedFields || {};

    return {
      key: issue.key,
      id: issue.id,
      self: issue.self,
      url: `${this.baseUrl}/browse/${issue.key}`,
      summary: f.summary,
      description: rf.description || f.description || null,
      descriptionRaw: f.description,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      status: f.status ? { name: f.status.name, categoryKey: f.status.statusCategory?.key, color: f.status.statusCategory?.colorName } : null,
      issuetype: f.issuetype ? { name: f.issuetype.name, iconUrl: f.issuetype.iconUrl, subtask: f.issuetype.subtask } : null,
      priority: f.priority ? { name: f.priority.name, iconUrl: f.priority.iconUrl } : null,
      resolution: f.resolution ? { name: f.resolution.name } : null,
      assignee: f.assignee ? { displayName: f.assignee.displayName, accountId: f.assignee.accountId, avatarUrl: f.assignee.avatarUrls?.["32x32"] } : null,
      reporter: f.reporter ? { displayName: f.reporter.displayName, accountId: f.reporter.accountId, avatarUrl: f.reporter.avatarUrls?.["32x32"] } : null,
      creator: f.creator ? { displayName: f.creator.displayName, accountId: f.creator.accountId, avatarUrl: f.creator.avatarUrls?.["32x32"] } : null,
      created: f.created,
      updated: f.updated,
      resolutiondate: f.resolutiondate,
      duedate: f.duedate,
      timeTracking: {
        originalEstimate: f.timeoriginalestimate ? Math.round(f.timeoriginalestimate / 3600) : null,
        originalEstimateLabel: f.timetracking?.originalEstimate || (f.timeoriginalestimate ? this.formatSeconds(f.timeoriginalestimate) : null),
        timeSpent: f.timespent ? Math.round(f.timespent / 3600) : null,
        timeSpentLabel: f.timetracking?.timeSpent || (f.timespent ? this.formatSeconds(f.timespent) : null),
        remainingEstimate: f.timetracking?.remainingEstimate || null,
      },
      labels: f.labels || [],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      components: (f.components || []).map((c: any) => c.name),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      fixVersions: (f.fixVersions || []).map((v: any) => ({ name: v.name, released: v.released, releaseDate: v.releaseDate })),
      sprint: f.sprint ? { id: f.sprint.id, name: f.sprint.name, state: f.sprint.state, startDate: f.sprint.startDate, endDate: f.sprint.endDate } : null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      attachments: (f.attachment || []).map((a: any) => ({
        id: a.id, filename: a.filename, mimeType: a.mimeType, size: a.size,
        content: a.content, thumbnail: a.thumbnail, created: a.created, author: a.author?.displayName,
      })),
      storyPoints: f.story_points || f.customfield_10016 || null,
      parent: f.parent ? {
        key: f.parent.key, summary: f.parent.fields?.summary,
        status: f.parent.fields?.status?.name, issuetype: f.parent.fields?.issuetype?.name,
      } : null,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      subtasks: (f.subtasks || []).map((s: any) => ({
        key: s.key, summary: s.fields?.summary, status: s.fields?.status?.name,
        issuetype: s.fields?.issuetype?.name, assignee: s.fields?.assignee?.displayName,
      })),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      issueLinks: (f.issuelinks || []).map((link: any) => {
        const inward = link.inwardIssue;
        const outward = link.outwardIssue;
        const linked = inward || outward;
        return {
          type: link.type?.name,
          direction: inward ? "inward" : "outward",
          label: inward ? link.type?.inward : link.type?.outward,
          issue: linked ? {
            key: linked.key, summary: linked.fields?.summary, status: linked.fields?.status?.name,
            issuetype: linked.fields?.issuetype?.name, priority: linked.fields?.priority?.name,
          } : null,
        };
      }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      comments: comments.map((c: any) => ({
        id: c.id, author: { displayName: c.author?.displayName, avatarUrl: c.author?.avatarUrls?.["32x32"] },
        body: c.renderedBody || c.body, created: c.created, updated: c.updated,
      })),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      remoteLinks: remoteLinks.map((rl: any) => ({
        title: rl.object?.title, url: rl.object?.url, icon: rl.object?.icon?.url16x16,
      })),
      devInfo,
      devDetail,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      changelog: (issue.changelog?.histories || []).slice(0, 20).map((h: any) => ({
        id: h.id, author: h.author?.displayName, created: h.created,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        items: (h.items || []).map((item: any) => ({ field: item.field, from: item.fromString, to: item.toString })),
      })),
    };
  }

  private formatSeconds(secs: number): string {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h >= 40) {
      const w = Math.floor(h / 40);
      const remH = h % 40;
      const d = Math.floor(remH / 8);
      const finalH = remH % 8;
      const parts: string[] = [];
      if (w) parts.push(`${w}w`);
      if (d) parts.push(`${d}d`);
      if (finalH) parts.push(`${finalH}h`);
      return parts.join(" ") || "0h";
    }
    if (h >= 8) {
      const d = Math.floor(h / 8);
      const remH = h % 8;
      return remH > 0 ? `${d}d ${remH}h` : `${d}d`;
    }
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  }

  // ==================== Dev Info ====================

  async getIssueDevInfo(issueKey: string): Promise<{ commits: number; pullRequests: number }> {
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const data = await this.request<any>(
        `/rest/dev-status/latest/issue/summary?issueId=${issueKey}`
      );
      return {
        commits: data?.summary?.commit?.overall?.count || 0,
        pullRequests: data?.summary?.pullrequest?.overall?.count || 0,
      };
    } catch {
      return { commits: 0, pullRequests: 0 };
    }
  }

  async getIssueDevDetail(issueId: string): Promise<{
    commits: { id: string; message: string; author: string; authorDate: string; url: string; repo: string; files?: number }[];
    pullRequests: { id: string; title: string; author: string; status: string; url: string; repo: string; source?: string; destination?: string }[];
  }> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const commits: any[] = [];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const pullRequests: any[] = [];

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const commitData = await this.request<any>(
        `/rest/dev-status/latest/issue/detail?issueId=${issueId}&applicationType=stash&dataType=repository`
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      for (const detail of commitData?.detail || []) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const repo of detail?.repositories || []) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          for (const c of repo?.commits || []) {
            commits.push({
              id: c.id || c.displayId, message: c.message,
              author: c.author?.name || "", authorDate: c.authorTimestamp,
              url: c.url, repo: repo.name || "", files: c.fileCount,
            });
          }
        }
      }

      // Also try GitHub sources
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bbData = await this.request<any>(
        `/rest/dev-status/latest/issue/detail?issueId=${issueId}&applicationType=GitHub&dataType=repository`
      ).catch(() => null);

      if (bbData) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        for (const detail of bbData?.detail || []) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          for (const repo of detail?.repositories || []) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            for (const c of repo?.commits || []) {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              if (!commits.find((existing: any) => existing.id === (c.id || c.displayId))) {
                commits.push({
                  id: c.id || c.displayId, message: c.message,
                  author: c.author?.name || "", authorDate: c.authorTimestamp,
                  url: c.url, repo: repo.name || "", files: c.fileCount,
                });
              }
            }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            for (const pr of repo?.pullRequests || []) {
              pullRequests.push({
                id: pr.id, title: pr.name || pr.title,
                author: pr.author?.name || "", status: pr.status,
                url: pr.url, repo: repo.name || "",
                source: pr.source?.branch, destination: pr.destination?.branch,
              });
            }
          }
        }
      }
    } catch (e) {
      console.error("[Jira] Failed to fetch dev detail:", e);
    }

    return { commits, pullRequests };
  }

  private async batchGetDevInfo(
    issueIds: string[],
    concurrency = 10
  ): Promise<Map<string, { commits: number; pullRequests: number }>> {
    const results = new Map<string, { commits: number; pullRequests: number }>();
    const chunks: string[][] = [];
    for (let i = 0; i < issueIds.length; i += concurrency) {
      chunks.push(issueIds.slice(i, i + concurrency));
    }
    for (const chunk of chunks) {
      const batch = await Promise.all(
        chunk.map(async (id) => ({ id, info: await this.getIssueDevInfo(id) }))
      );
      for (const { id, info } of batch) {
        results.set(id, info);
      }
    }
    return results;
  }

  // ==================== ACTIVITY FEED ====================

  async getActivityFeed(
    projectKey: string,
    lookbackDays: number = 90,
    boardId?: number,
    startDate?: string,
    endDate?: string
  ): Promise<JiraActivityFeed> {
    const since = startDate || new Date(Date.now() - lookbackDays * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    let until: string | undefined;
    if (endDate) {
      const end = new Date(endDate);
      end.setDate(end.getDate() + 1);
      until = end.toISOString().split("T")[0];
    }
    let jql = `project = "${projectKey}" AND updated >= "${since}"`;
    if (until) jql += ` AND updated < "${until}"`;
    jql += ` ORDER BY updated DESC`;
    const issues = await this.searchIssuesWithChangelog(jql, [
      "summary", "status", "issuetype", "assignee", "reporter",
      "priority", "created", "updated", "resolutiondate",
      "components", "labels", "sprint", "parent",
      "timeoriginalestimate", "timespent", "comment",
    ]);

    const events: JiraActivityEvent[] = [];
    const statusCounts = new Map<string, number>();
    const typeCounts = new Map<string, number>();

    for (const issue of issues) {
      const itype = issue.fields.issuetype.name;
      typeCounts.set(itype, (typeCounts.get(itype) || 0) + 1);
      const currentStatus = issue.fields.status.name;
      statusCounts.set(currentStatus, (statusCounts.get(currentStatus) || 0) + 1);

      const issueMeta = {
        issueKey: issue.key,
        issueSummary: issue.fields.summary,
        issueType: itype,
        issueTypeIcon: issue.fields.issuetype.iconUrl || undefined,
        isSubtask: issue.fields.issuetype.subtask || false,
        parent: issue.fields.parent ? {
          key: issue.fields.parent.key,
          summary: issue.fields.parent.fields?.summary || "",
          issueType: issue.fields.parent.fields?.issuetype?.name,
        } : undefined,
        labels: (issue.fields.labels && issue.fields.labels.length > 0) ? issue.fields.labels : undefined,
        priority: issue.fields.priority?.name || undefined,
        priorityIcon: issue.fields.priority?.iconUrl || undefined,
      };

      events.push({
        ...issueMeta,
        id: `${issue.key}-created`,
        timestamp: issue.fields.created,
        type: "created",
        author: {
          displayName: issue.fields.reporter?.displayName || "Unknown",
          accountId: issue.fields.reporter?.accountId || "",
          avatarUrl: issue.fields.reporter?.avatarUrls?.["48x48"],
        },
      });

      const histories = issue.changelog?.histories || [];
      for (const history of histories) {
        for (const item of history.items) {
          const baseEvent = {
            ...issueMeta,
            author: {
              displayName: history.author.displayName,
              accountId: history.author.accountId,
              avatarUrl: history.author.avatarUrls?.["48x48"],
            },
          };

          if (item.field === "status") {
            events.push({ ...baseEvent, id: `${issue.key}-${history.id}-status`, timestamp: history.created, type: "status_change", fromStatus: item.fromString || undefined, toStatus: item.toString || undefined });
          } else if (item.field === "assignee") {
            events.push({ ...baseEvent, id: `${issue.key}-${history.id}-assignee`, timestamp: history.created, type: "assignment", fromAssignee: item.fromString || undefined, toAssignee: item.toString || undefined });
          } else if (item.field === "Sprint") {
            events.push({ ...baseEvent, id: `${issue.key}-${history.id}-sprint`, timestamp: history.created, type: "sprint_change", fromSprint: item.fromString || undefined, toSprint: item.toString || undefined });
          } else if (item.field === "priority") {
            events.push({ ...baseEvent, id: `${issue.key}-${history.id}-priority`, timestamp: history.created, type: "priority_change", fromValue: item.fromString || undefined, toValue: item.toString || undefined, fieldName: "priority" });
          } else if (item.field === "resolution" && item.toString) {
            events.push({ ...baseEvent, id: `${issue.key}-${history.id}-resolved`, timestamp: history.created, type: "resolved", toValue: item.toString || undefined });
          }
        }
      }

      // Process comments
      const comments = issue.fields.comment?.comments || [];
      for (const comment of comments) {
        // Extract plain text from Jira's ADF (Atlassian Document Format) body
        let bodyText = '';
        if (comment.body && typeof comment.body === 'object') {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const extractText = (node: any): string => {
            if (node.type === 'text') return node.text || '';
            if (node.content && Array.isArray(node.content)) return node.content.map(extractText).join('');
            return '';
          };
          bodyText = extractText(comment.body).slice(0, 200);
        } else if (typeof comment.body === 'string') {
          bodyText = comment.body.slice(0, 200);
        }

        events.push({
          ...issueMeta,
          id: `${issue.key}-comment-${comment.id}`,
          timestamp: comment.created,
          type: "comment",
          author: {
            displayName: comment.author?.displayName || "Unknown",
            accountId: comment.author?.accountId || "",
            avatarUrl: comment.author?.avatarUrls?.["48x48"],
          },
          commentBody: bodyText || undefined,
        });
      }
    }

    const sinceMs = new Date(since).getTime();
    const untilMs = until ? new Date(until).getTime() : Infinity;
    const filteredEvents = events.filter((e) => {
      const ts = new Date(e.timestamp).getTime();
      return ts >= sinceMs && ts < untilMs;
    });
    filteredEvents.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Build developer list from actual event authors (not issue assignees)
    const devCounts = new Map<string, { displayName: string; accountId: string; avatarUrl?: string; count: number }>();
    for (const ev of filteredEvents) {
      const key = ev.author.accountId || ev.author.displayName;
      if (!devCounts.has(key)) {
        devCounts.set(key, {
          displayName: ev.author.displayName,
          accountId: ev.author.accountId,
          avatarUrl: ev.author.avatarUrl,
          count: 0,
        });
      }
      devCounts.get(key)!.count++;
    }

    let sprintSummary: JiraActivityFeed["sprintSummary"] = [];
    if (boardId) {
      try {
        const sprints = await this.getSprints(boardId);
        const recentSprints = sprints.filter((s) => s.state === "active" || s.state === "closed").slice(-10);
        const sprintResults = await Promise.all(
          recentSprints.map(async (sprint) => {
            const { total, done } = await this.getSprintIssueCount(sprint.id);
            return {
              name: sprint.name, state: sprint.state, totalIssues: total,
              completedIssues: done, startDate: sprint.startDate, endDate: sprint.endDate,
            };
          })
        );
        sprintSummary = sprintResults;
      } catch { /* empty */ }
    }

    return {
      events: filteredEvents,
      totalEvents: filteredEvents.length,
      developers: Array.from(devCounts.values()).map((d) => ({ ...d, issueCount: d.count })).sort((a, b) => b.issueCount - a.issueCount),
      statusBreakdown: Array.from(statusCounts.entries()).map(([status, count]) => ({ status, count })).sort((a, b) => b.count - a.count),
      issueTypeBreakdown: Array.from(typeCounts.entries()).map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count),
      sprintSummary,
      dateRange: { start: since, end: new Date().toISOString().split("T")[0] },
    };
  }

  // ==================== LAG INDICATORS ====================

  async calculateLagIndicators(
    projectKey: string,
    developers: string[],
    lookbackDays: number = 90,
    startDate?: string,
    endDate?: string
  ): Promise<LagIndicatorResult[]> {
    const since = startDate || new Date(Date.now() - lookbackDays * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    let until: string | undefined;
    if (endDate) {
      const end = new Date(endDate);
      end.setDate(end.getDate() + 1);
      until = end.toISOString().split("T")[0];
    }

    let jql = `project = "${projectKey}" AND updated >= "${since}"`;
    if (until) jql += ` AND updated < "${until}"`;
    jql += ` ORDER BY updated DESC`;
    const issues = await this.searchIssuesWithChangelog(jql);

    const devMap = new Map<string, string>();
    for (const issue of issues) {
      if (issue.fields.assignee) {
        const name = issue.fields.assignee.displayName.toLowerCase();
        if (!devMap.has(name)) devMap.set(name, issue.fields.assignee.accountId);
      }
    }

    const targetDevs = developers.length > 0
      ? developers.map((d) => d.toLowerCase())
      : Array.from(devMap.keys());

    const devInfoIds = getDevInfoIndicatorIds("lag");
    let devInfo = new Map<string, { commits: number; pullRequests: number }>();
    if (devInfoIds.length > 0) {
      const allIssueIds = issues
        .filter((i) => isDoneStatus(i.fields.status.name))
        .map((i) => String(i.id));
      devInfo = await this.batchGetDevInfo([...new Set(allIssueIds)]);
    }

    return runAllIndicators("lag", issues, targetDevs, devMap, devInfo);
  }

  // ==================== LEAD INDICATORS ====================

  async calculateLeadIndicators(
    projectKey: string,
    developers: string[],
    lookbackDays: number = 90,
    startDate?: string,
    endDate?: string
  ): Promise<LagIndicatorResult[]> {
    const since = startDate || new Date(Date.now() - lookbackDays * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
    let until: string | undefined;
    if (endDate) {
      const end = new Date(endDate);
      end.setDate(end.getDate() + 1);
      until = end.toISOString().split("T")[0];
    }

    let jql = `project = "${projectKey}" AND updated >= "${since}"`;
    if (until) jql += ` AND updated < "${until}"`;
    jql += ` ORDER BY updated DESC`;
    const issues = await this.searchIssuesWithChangelog(jql);

    const devMap = new Map<string, string>();
    for (const issue of issues) {
      if (issue.fields.assignee) {
        const name = issue.fields.assignee.displayName.toLowerCase();
        if (!devMap.has(name)) devMap.set(name, issue.fields.assignee.accountId);
      }
    }

    const targetDevs = developers.length > 0
      ? developers.map((d) => d.toLowerCase())
      : Array.from(devMap.keys());

    const devInfoIds = getDevInfoIndicatorIds("lead");
    let devInfo = new Map<string, { commits: number; pullRequests: number }>();
    if (devInfoIds.length > 0) {
      const allIssueIds = issues
        .filter((i) => isDoneStatus(i.fields.status.name))
        .map((i) => String(i.id));
      devInfo = await this.batchGetDevInfo([...new Set(allIssueIds)]);
    }

    return runAllIndicators("lead", issues, targetDevs, devMap, devInfo);
  }
}

// ==================== Standalone validation (for settings page) ====================

export async function validateJiraCredentials(config: JiraConfig): Promise<{
  valid: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  user?: any;
  authMethod?: string;
  error?: string;
}> {
  const domain = config.domain.trim().replace(/^https?:\/\//, "").replace(/\.atlassian\.net\/?$/, "").trim();
  const email = config.email.trim();
  const apiToken = config.apiToken.trim();
  const baseUrl = `https://${domain}.atlassian.net`;

  try {
    // Strategy 1: Basic auth
    const basicAuth = buildBasicAuth(email, apiToken);
    const basicRes = await jiraFetch(`${baseUrl}/rest/api/3/myself`, {
      headers: { Authorization: basicAuth, Accept: "application/json" },
    });

    if (basicRes.ok) {
      const user = await basicRes.json();
      return { valid: true, user, authMethod: "basic" };
    }

    // Strategy 2: Bearer token
    const bearerRes = await jiraFetch(`${baseUrl}/rest/api/3/myself`, {
      headers: { Authorization: `Bearer ${apiToken}`, Accept: "application/json" },
    });

    if (bearerRes.ok) {
      const user = await bearerRes.json();
      return { valid: true, user, authMethod: "bearer" };
    }

    return {
      valid: false,
      error: `Authentication failed. Make sure you're using a classic API token from:\nhttps://id.atlassian.com/manage-profile/security/api-tokens`,
    };
  } catch (error: unknown) {
    const err = error as Error;
    return { valid: false, error: err.message || "Validation failed" };
  }
}
